import { type FormEvent, type ReactElement, useCallback, useEffect, useState } from 'react';
import { type ExportReceiptView, type ExportRecordView, type ExportResult } from '@vello/api-client';
import { formatMarketInstant, marketDate, marketDayEnd, marketDayStart } from '../../src/index';
import { PersonName } from '../components/person-name';
import { useScopeZone } from '../components/scope-zone';
import { useAdmin } from '../session';
import { IdRef } from '../components/code-ref';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { usePlaceNames } from '../i18n/scope-name';
import { AUTO_REFRESH_MS, useAutoRefresh, Badge, Button, Field, Notice, PageHeader, Panel, StatCard, Table, TextInput, EmptyState, Grid, Stack, Text, PanelHead, FormGrid } from '@vello/web-theme';

/**
 * Relatórios (D318) — the operator analytics desk over the real S114 export surface: run a **privacy-minimized
 * export** over a window (`POST /v1/analytics/exports`, operator `report:export` — the server strips sensitive
 * properties and user ids before a batch leaves the platform, and reports the keys it dropped) and read the
 * auditable **export receipts** (`GET /v1/analytics/exports`). Windows are entered as calendar dates and sent
 * as UTC instants; no aggregate is invented client-side — the counts are the server's.
 */
const WEEK_MS = 7 * 86_400_000;

export function ReportsScreen(): ReactElement {
  const { runtime, scope } = useAdmin();
  const { t, tp } = useT();
  const { v, fail } = useVocab();
  const place = usePlaceNames();
  // D420 (Admin checklist #2): the export window is typed and read on the scope's market wall clock (a day is its LOCAL
  // midnight to midnight); UTC, marked, when the scope has no single zone.
  const { zone, lang } = useScopeZone();
  const dayStartIso = (day: string): string => marketDayStart(day, zone) ?? `${day}T00:00:00.000Z`;
  const dayEndIso = (day: string): string => marketDayEnd(day, zone) ?? `${day}T23:59:59.999Z`;
  const when = (iso: string, style: 'date' | 'datetime' | 'datetime-seconds' = 'datetime'): string => formatMarketInstant(iso, zone, lang, style);
  const [receipts, setReceipts] = useState<readonly ExportReceiptView[] | undefined>(undefined);
  const [listError, setListError] = useState<string | undefined>(undefined);

  const [from, setFrom] = useState(() => marketDate(new Date(Date.now() - WEEK_MS), zone));
  const [to, setTo] = useState(() => marketDate(new Date(), zone));
  const [name, setName] = useState('');
  // D580: the top-bar scope fixes the country; the field exists only where the scope spans countries (a platform-level operator).
  const [typedCountry, setCountry] = useState('');
  const country = scope.current.country ?? typedCountry;
  const [limit, setLimit] = useState('200');
  const [result, setResult] = useState<ExportResult | undefined>(undefined);
  const [msg, setMsg] = useState<{ tone: 'success' | 'danger'; text: string } | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const loadReceipts = useCallback(async (): Promise<void> => {
    const res = await runtime.analytics.listExports();
    if (res.ok) {
      setReceipts(res.value.exports);
      setListError(undefined);
    } else {
      setReceipts((prev) => prev ?? []);
      setListError(t('rep.error.list', { code: fail(res.error) }));
    }
  }, [runtime, t, fail]);

  useEffect(() => {
    void loadReceipts();
  }, [loadReceipts]);
  useAutoRefresh(loadReceipts, { intervalMs: AUTO_REFRESH_MS.report });

  const runExport = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (from === '' || to === '' || from > to) {
      setMsg({ tone: 'danger', text: t('rep.invalidWindow') });
      return;
    }
    setBusy(true);
    setMsg(undefined);
    const n = Number.parseInt(limit, 10);
    const res = await runtime.analytics.export({
      from: dayStartIso(from),
      to: dayEndIso(to),
      ...(name.trim() !== '' ? { name: name.trim() } : {}),
      ...(country.trim() !== '' ? { country: country.trim().toUpperCase() } : {}),
      ...(Number.isInteger(n) && n > 0 ? { limit: n } : {}),
    });
    setBusy(false);
    if (res.ok) {
      setResult(res.value);
      setMsg({ tone: 'success', text: tp('rep.exported', res.value.recordCount, { id: res.value.exportId }) });
      void loadReceipts();
    } else {
      setResult(undefined);
      setMsg({ tone: 'danger', text: t('rep.error.export', { code: fail(res.error) }) });
    }
  };

  const droppedTotal = result !== undefined ? result.records.reduce((acc, r) => acc + r.droppedKeys.length, 0) : 0;

  return (
    <>
      <PageHeader title={t('rep.title')} subtitle={t('rep.subtitle')} />

      <Panel>
        <Stack gap={4}>
          <Text as="h2" role="title">{t('rep.new.title')}</Text>
          <Text as="p" role="caption" tone="secondary">{t('rep.privacy.note')}</Text>
          <form onSubmit={(e) => void runExport(e)} noValidate>
            <Stack gap={4}>
              <FormGrid>
                <Field label={t('rep.field.from')} htmlFor="rep-from">
                  <TextInput id="rep-from" className="tnum" type="date" value={from} onChange={(e) => setFrom(e.target.value)} required />
                </Field>
                <Field label={t('rep.field.to')} htmlFor="rep-to">
                  <TextInput id="rep-to" className="tnum" type="date" value={to} onChange={(e) => setTo(e.target.value)} required />
                </Field>
                <Field label={t('rep.field.name')} htmlFor="rep-name" hint={t('rep.field.name.hint')}>
                  <TextInput id="rep-name" className="tnum" value={name} onChange={(e) => setName(e.target.value)} />
                </Field>
                {scope.current.country === undefined && (
                  <Field label={t('rep.field.country')} htmlFor="rep-country" hint="ISO 3166-1">
                    <TextInput id="rep-country" value={country} maxLength={2} onChange={(e) => setCountry(e.target.value.toUpperCase())} />
                  </Field>
                )}
                <Field label={t('rep.field.limit')} htmlFor="rep-limit">
                  <TextInput id="rep-limit" className="tnum" inputMode="numeric" value={limit} onChange={(e) => setLimit(e.target.value)} />
                </Field>
              </FormGrid>
              {msg !== undefined && (
                <Notice tone={msg.tone}>{msg.text}</Notice>
              )}
              <Button type="submit" variant="primary" disabled={busy}>
                {busy ? t('rep.export.busy') : t('rep.export')}
              </Button>
            </Stack>
          </form>
        </Stack>
      </Panel>

      {result !== undefined && (
        <>
          <Grid min="xs" gap={4}>
            <StatCard label={t('rep.kpi.records')} value={String(result.recordCount)} hint={result.exportId} />
            <StatCard label={t('rep.kpi.shown')} value={String(result.records.length)} />
            <StatCard label={t('rep.kpi.dropped')} value={String(droppedTotal)} hint={t('rep.kpi.dropped.hint')} />
          </Grid>
          <Panel padded={false}>
            <PanelHead title={t('rep.records.title')} />
            <Table
              columns={[
                { key: 'event', header: t('rep.col.event'), render: (r: ExportRecordView) => <IdRef value={r.eventId} /> },
                { key: 'name', header: t('rep.col.name'), render: (r: ExportRecordView) => <span>{v('analytics.event_name', r.name)}</span> },
                { key: 'version', header: t('rep.col.version'), numeric: true, render: (r: ExportRecordView) => String(r.version) },
                { key: 'at', header: t('rep.col.occurred'), render: (r: ExportRecordView) => <span className="tnum">{when(r.occurredAt, 'datetime-seconds')}</span> },
                { key: 'actor', header: t('rep.col.actor'), render: (r: ExportRecordView) => <Badge tone="neutral">{v('analytics.event_actor', r.actor)}</Badge> },
                { key: 'where', header: t('rep.col.where'), render: (r: ExportRecordView) => [r.country !== undefined ? place.country(r.country) : undefined, r.city !== undefined ? place.market(r.city) : undefined].filter((s) => s !== undefined).join(' · ') || '—' },
                { key: 'dropped', header: t('rep.col.dropped'), numeric: true, render: (r: ExportRecordView) => String(r.droppedKeys.length) },
              ]}
              rows={result.records}
              rowKey={(r) => r.eventId}
              empty={<EmptyState title={t('rep.records.empty')} />}
            />
          </Panel>
        </>
      )}

      {listError !== undefined && (
        <Notice tone="danger">{listError}</Notice>
      )}

      <Panel padded={false}>
        <PanelHead title={t('rep.receipts.title')} />
        <Table
          columns={[
            { key: 'id', header: t('rep.col.export'), render: (r: ExportReceiptView) => <IdRef value={r.exportId} /> },
            { key: 'op', header: t('rep.col.operator'), render: (r: ExportReceiptView) => <PersonName id={r.operatorId} /> },
            { key: 'window', header: t('rep.col.window'), render: (r: ExportReceiptView) => <span className="tnum">{`${when(r.from, 'date')} → ${when(r.to, 'date')}`}</span> },
            { key: 'name', header: t('rep.col.name'), render: (r: ExportReceiptView) => r.name ?? '—' },
            { key: 'country', header: t('rep.col.country'), render: (r: ExportReceiptView) => place.country(r.country) },
            { key: 'count', header: t('rep.col.count'), numeric: true, render: (r: ExportReceiptView) => String(r.recordCount) },
            { key: 'created', header: t('rep.col.created'), render: (r: ExportReceiptView) => <span className="tnum">{when(r.createdAt)}</span> },
          ]}
          rows={receipts ?? []}
          rowKey={(r) => r.exportId}
          loading={receipts === undefined}
          loadingLabel={t('ui.loading')}
          empty={<EmptyState title={t('rep.receipts.empty')} />}
        />
      </Panel>
    </>
  );
}
